import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronUp, HelpCircle, Send } from 'lucide-react';
import api from '../lib/api';

export default function SupportPage() {
  const navigate = useNavigate();

  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ category: 'technical', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/support/faqs')
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data?.faqs) ? data.faqs : Array.isArray(data) ? data : [];
        setFaqs(list);
      })
      .catch(console.error);
  }, []);

  const handleField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Subject and message are required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/api/support/tickets', {
        category: form.category,
        subject: form.subject,
        message: form.message,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const categories = [
    { value: 'account', label: 'Account' },
    { value: 'billing', label: 'Billing' },
    { value: 'technical', label: 'Technical' },
    { value: 'medical-records', label: 'Medical Records' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', minHeight: '100vh', background: 'var(--BG)', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e5e5' }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 10, display: 'flex', alignItems: 'center', color: 'var(--primary)' }}
        >
          <ChevronLeft size={22} />
        </button>
        <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>Help & Support</h5>
      </div>

      <div style={{ padding: '20px 16px' }}>
        {/* FAQs */}
        {faqs.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <h6 style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HelpCircle size={16} color="var(--primary)" /> Frequently Asked Questions
            </h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  style={{ background: '#f9f7ff', borderRadius: 12, border: '1px solid rgba(127,0,255,0.08)', overflow: 'hidden' }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{faq.question}</span>
                    {openFaq === idx ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {openFaq === idx && (
                    <div style={{ padding: '0 14px 14px', fontSize: 13, color: '#555', lineHeight: 1.6 }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ticket form */}
        <h6 style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 14 }}>Submit a Support Ticket</h6>

        {submitted ? (
          <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#2e7d32' }}>Ticket submitted successfully!</p>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#388e3c' }}>Our team will get back to you shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#664d03' }}>
                {error}
              </div>
            )}

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Category</label>
              <select
                value={form.category}
                onChange={(e) => handleField('category', e.target.value)}
                style={{ width: '100%', border: '1px solid #ddd', borderRadius: 10, padding: '10px 12px', fontSize: 13, background: '#fff' }}
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Subject</label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => handleField('subject', e.target.value)}
                placeholder="Brief description of your issue"
                required
                style={{ width: '100%', border: '1px solid #ddd', borderRadius: 10, padding: '10px 12px', fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Message</label>
              <textarea
                value={form.message}
                onChange={(e) => handleField('message', e.target.value)}
                placeholder="Describe your issue in detail..."
                rows={5}
                required
                style={{ width: '100%', border: '1px solid #ddd', borderRadius: 10, padding: '10px 12px', fontSize: 13, background: '#fff', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 0', borderRadius: 12, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
            >
              <Send size={16} /> {submitting ? 'Submitting...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
